import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Response structure from Brønnøysund Register Centre API
 * https://data.brreg.no/enhetsregisteret/api/enheter/{organisasjonsnummer}
 */
export interface BrregCompanyData {
  organisasjonsnummer: string;
  navn: string;
  organisasjonsform?: {
    kode: string;
    beskrivelse: string;
  };
  forretningsadresse?: {
    adresse?: string[];
    postnummer?: string;
    poststed?: string;
    land?: string;
    kommune?: string;
    kommunenummer?: string;
  };
  postadresse?: {
    adresse?: string[];
    postnummer?: string;
    poststed?: string;
    land?: string;
  };
  registreringsdatoEnhetsregisteret?: string;
  antallAnsatte?: number;
  naeringskode1?: {
    kode: string;
    beskrivelse: string;
  };
  institusjonellSektorkode?: {
    kode: string;
    beskrivelse: string;
  };
  // Extended BRREG fields
  konkurs?: boolean;
  underAvvikling?: boolean;
  underTvangsavviklingEllerTvangsopplosning?: boolean;
  overordnetEnhet?: string;
  registrertIMvaregisteret?: boolean;
  registreringsdatoMerverdiavgiftsregisteret?: string;
  sisteInnsendteAarsregnskap?: string;
  aktivitet?: string[];
  paategninger?: Array<{
    type?: string;
    beskrivelse?: string;
    dato?: string;
  }>;
  _links?: {
    self?: {
      href?: string;
    };
  };
  // Contact + meta fields
  hjemmeside?: string;
  epostadresse?: string;
  mobil?: string;
  telefon?: string;
  erIKonsern?: boolean;
  maalform?: string;
}

/**
 * Response structure for name search (returns a list)
 */
export interface BrregSearchResponse {
  _embedded?: {
    enheter?: BrregCompanyData[];
  };
  page?: {
    size: number;
    totalElements: number;
    totalPages: number;
    number: number;
  };
}

interface UseBrregCompanySearchReturn {
  search: (query: string) => Promise<BrregCompanyData[] | null>;
  isLoading: boolean;
  error: string | null;
}

const BRREG_API_URL = 'https://data.brreg.no/enhetsregisteret/api/enheter';

export const useBrregCompanySearch = (): UseBrregCompanySearchReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('companies');

  const search = useCallback(async (query: string): Promise<BrregCompanyData[] | null> => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setError(t('searchModal.pleaseEnterNameOrOrgNumber'));
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const cleanedQuery = trimmedQuery.replace(/\s+/g, '');
      const isOrganizationNumber = /^\d{9}$/.test(cleanedQuery);

      if (isOrganizationNumber) {
        const response = await fetch(`${BRREG_API_URL}/${cleanedQuery}`);

        if (!response.ok) {
          if (response.status === 404) {
            return [];
          }
          throw new Error(t('searchModal.failedToFetch'));
        }

        const data: BrregCompanyData = await response.json();
        return [data];
      } else {
        const encodedName = encodeURIComponent(trimmedQuery);
        const response = await fetch(`${BRREG_API_URL}?navn=${encodedName}&size=10`);

        if (!response.ok) {
          throw new Error(t('searchModal.failedToFetch'));
        }

        const searchData: BrregSearchResponse = await response.json();
        const companies = searchData._embedded?.enheter || [];

        return companies;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('searchModal.failedToFetch');
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    search,
    isLoading,
    error,
  };
};
